import type { WhackPop, WhackSetup, WhackTap } from '@goodtime/shared';

/**
 * Whack a mole, a speed round, as plain logic: which mole a tap hits, the
 * streak, the prize ladder and the words. The server sends the schedule
 * at the start and counts the hits itself at the finish (each pop at most
 * once, inside its window); this only has to agree with it.
 */

/** Slack for a tap at the very edge of a mole's time up (the server allows more). */
export const TAP_GRACE_MS = 60;
/** A mole takes this long to come up and to go down. */
export const RISE_MS = 110;
/** A hit mole stays up, dazed, this long before it drops. */
export const DAZED_MS = 320;

/** When the last mole is down (ms from GO). */
export const roundEnd = (pops: readonly WhackPop[]): number => pops.reduce((end, p) => Math.max(end, p.atMs + p.upMs), 0);

/** The mole up in `hole` at `t` (ms from GO) that hasn't been hit, as its
 *  index in the schedule, or -1. */
export function popAt(pops: readonly WhackPop[], hole: number, t: number, hit: ReadonlySet<number>): number {
  for (let i = 0; i < pops.length; i++) {
    const p = pops[i]!;
    if (p.hole === hole && !hit.has(i) && t >= p.atMs - TAP_GRACE_MS && t <= p.atMs + p.upMs + TAP_GRACE_MS) return i;
  }
  return -1;
}

/** How a mole in `hole` shows at `t`: how far up (0 to 1), whether it's
 *  dazed from a hit, and its index (-1 for none). */
export function holeAt(
  pops: readonly WhackPop[],
  hole: number,
  t: number,
  hitAt: ReadonlyMap<number, number>,
): { pop: number; up: number; dazed: boolean } {
  for (let i = 0; i < pops.length; i++) {
    const p = pops[i]!;
    if (p.hole !== hole) continue;
    const struck = hitAt.get(i);
    // A hit mole stays up a moment, dazed, then drops; never past its own
    // time, so the next mole in this hole always shows.
    const down = struck !== undefined ? Math.min(struck + DAZED_MS, p.atMs + p.upMs) : p.atMs + p.upMs;
    if (t < p.atMs || t > down + RISE_MS) continue;
    const rising = Math.min(1, (t - p.atMs) / RISE_MS);
    const falling = t > down ? 1 - (t - down) / RISE_MS : 1;
    return { pop: i, up: Math.max(0, Math.min(rising, falling)), dazed: struck !== undefined && t >= struck };
  }
  return { pop: -1, up: 0, dazed: false };
}

/** Moles that went down untapped by `t`: each one breaks the streak. */
export function missedBy(pops: readonly WhackPop[], t: number, hit: ReadonlySet<number>): number {
  let missed = 0;
  pops.forEach((p, i) => {
    if (!hit.has(i) && p.atMs + p.upMs + TAP_GRACE_MS < t) missed++;
  });
  return missed;
}

/** The step of the prize ladder `hits` reached (0 is the first: the floor,
 *  under the first step). */
export function bandOf(setup: Pick<WhackSetup, 'bands'>, hits: number): number {
  let band = 0;
  setup.bands.forEach((at, i) => {
    if (hits >= at) band = i;
  });
  return band;
}

/** What `hits` pays: the prize of the step reached. */
export const bandPay = (setup: Pick<WhackSetup, 'bands' | 'pays'>, hits: number): number => setup.pays[bandOf(setup, hits)] ?? 0;

/** The next step up from `hits`: how many hits it takes and what it pays,
 *  or null at the top. */
export function nextBand(setup: Pick<WhackSetup, 'bands' | 'pays'>, hits: number): { at: number; pay: number } | null {
  const i = bandOf(setup, hits) + 1;
  return i < setup.bands.length ? { at: setup.bands[i]!, pay: setup.pays[i]! } : null;
}

/** Whether a finished round's hits reached a step of the ladder (the floor
 *  below the first step is a loss, whatever it pays). */
export const whackWon = (setup: Pick<WhackSetup, 'bands'>, hits: number): boolean => bandOf(setup, hits) > 0;

/** When the pace steps up, for the "Faster!" call: a third and two
 *  thirds of the way through the moles. */
export function fasterAt(pops: readonly WhackPop[]): number[] {
  const n = pops.length;
  if (n < 6) return [];
  return [pops[Math.floor(n / 3)]!.atMs, pops[Math.floor((2 * n) / 3)]!.atMs];
}

/** The taps as the server reads them. */
export const tapsArg = (taps: readonly WhackTap[]): string => JSON.stringify(taps.map((t) => ({ pop: t.pop, tMs: Math.round(t.tMs) })));

const hitsWord = (n: number) => `${n} ${n === 1 ? 'hit' : 'hits'}`;

export type WhackStage = 'ready' | 'countdown' | 'round' | 'counting' | 'over';

/** The line under the title. */
export function whackLead(stage: WhackStage, setup: WhackSetup | undefined, hits: number): string {
  switch (stage) {
    case 'ready':
    case 'countdown':
    case 'round':
      return 'Whack the moles as they pop up. More hits, bigger prize.';
    case 'counting':
      return 'Counting your hits…';
    case 'over': {
      if (!setup) return hitsWord(hits);
      const next = nextBand(setup, hits);
      if (!whackWon(setup, hits)) return `Only ${hitsWord(hits)}. Hit ${next?.at ?? 0} for a bigger prize.`;
      return next
        ? `${hitsWord(hits)}! Hit ${next.at} next time for ${next.pay.toLocaleString('en-GB')} coins.`
        : `${hitsWord(hits)}! The top prize!`;
    }
  }
}

/** A streak worth calling out, or ''. */
export function streakWords(streak: number): string {
  if (streak >= 10) return `${streak} in a row! On fire!`;
  if (streak >= 3) return `${streak} in a row!`;
  return '';
}

/** What things mean, said on a tap. */
export const WHACK_EXPLAIN = {
  meter: (setup: WhackSetup | undefined) => {
    if (!setup || setup.bands.length < 2) return 'Every mole you whack lights a lamp.';
    const first = setup.bands[1]!;
    const top = setup.bands[setup.bands.length - 1]!;
    const best = setup.pays[setup.pays.length - 1]!.toLocaleString('en-GB');
    return (
      `Every mole you whack lights a lamp. Reach a big lamp to win the coins over it: the first at ${first} hits, ` +
      `the top prize of ${best} coins at ${top}. They pop up faster as the round goes on.`
    );
  },
  counters: (setup: WhackSetup | undefined) =>
    `Hits counts the moles you've whacked. The round lasts ${Math.round((setup?.minMs ?? 8000) / 1000)} seconds.`,
};
